/**
 * Supplier Order Tracker — replaces George's per-supplier Google Sheets.
 *
 * - Filter by supplier (Lux Medical, Eviva, Getinge, ...) or "All"
 * - Status pipeline view: Open → Submitted → BO → Shipped → Paid
 * - Kanban OR table view toggle
 * - Click hospital → mailto draft
 * - Aging badge (red >14 days, amber 8-14, green ≤7)
 * - Margin column (hospital invoice − supplier cost)
 * - Add / Edit modal · Delete · Status quick-update
 * - Per-supplier Excel export mirroring Lux Medical sheet layout
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Download,
  Search,
  Mail,
  AlertCircle,
  Trash2,
  Pencil,
  X,
  Filter,
  TrendingUp,
  ClipboardList,
  Send,
  CheckSquare,
  Square,
  UserPlus,
  Users,
  Upload,
  MessageSquareReply,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  open: { label: "Open", chip: "bg-amber-100 text-amber-800 border-amber-300" },
  submitted_to_supplier: { label: "Submitted", chip: "bg-blue-100 text-blue-800 border-blue-300" },
  backordered: { label: "Backordered", chip: "bg-orange-100 text-orange-800 border-orange-300" },
  partially_shipped: { label: "Partial Ship", chip: "bg-purple-100 text-purple-800 border-purple-300" },
  shipped: { label: "Shipped", chip: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  delivered: { label: "Delivered", chip: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  invoiced: { label: "Invoiced", chip: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  paid: { label: "Paid", chip: "bg-green-100 text-green-800 border-green-300" },
  cancelled: { label: "Cancelled", chip: "bg-slate-100 text-slate-600 border-slate-300" },
};
const STATUS_OPTIONS = Object.keys(STATUS_META);

const formatMoney = (n) => {
  if (n === null || n === undefined) return "—";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const AgingBadge = ({ days, status }) => {
  if (status === "delivered" || status === "paid" || status === "cancelled") {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  let cls = "bg-emerald-100 text-emerald-800";
  if (days > 14) cls = "bg-red-100 text-red-800 font-bold";
  else if (days > 7) cls = "bg-amber-100 text-amber-800";
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid="order-aging-badge">
      {days}d
    </span>
  );
};

const SupplierOrdersManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hospitalFilter, setHospitalFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created");  // created | hospital | supplier | po | aging
  const [search, setSearch] = useState("");
  const [supplierPortalList, setSupplierPortalList] = useState([]); // from /api/suppliers
  const [showAddModal, setShowAddModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [summary, setSummary] = useState(null);
  // Bulk-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("");
  const [supplierEmailDraft, setSupplierEmailDraft] = useState(null);
  // Hospital email-status modal
  const [statusEmailOrder, setStatusEmailOrder] = useState(null);
  // Excel import
  const [showImportModal, setShowImportModal] = useState(false);
  // Paste reply modal
  const [showPasteReplyModal, setShowPasteReplyModal] = useState(false);
  // Sync Dashboards button — loading state to prevent double-submit
  const [syncing, setSyncing] = useState(false);

  const handleSyncDashboards = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/supplier-orders/sync-dashboards`,
        {},
        { headers },
      );
      const d = r.data || {};
      const visible = d.visible_to_hospital_portal ?? 0;
      const hiddenNoPrice = d.hidden_missing_price ?? 0;
      const hiddenNoHosp = d.hidden_missing_hospital ?? 0;
      toast.success(
        `Dashboards synchronized — ${visible} order${visible === 1 ? "" : "s"} now visible to hospital portals.`,
        {
          description:
            hiddenNoPrice + hiddenNoHosp > 0
              ? `${hiddenNoPrice} hidden (no hospital price set) · ${hiddenNoHosp} hidden (no hospital linked)`
              : `Backfilled ${d.hospital_ids_backfilled ?? 0} hospital links.`,
          duration: 6000,
        },
      );
      // Refresh the local table with whatever the backend now holds.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed. Try again.");
    } finally {
      setSyncing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/supplier-orders`, { headers });
      setOrders(r.data.orders || []);
      const s = await axios.get(`${API}/api/admin/supplier-orders/summary`, { headers });
      setSummary(s.data);
    } catch (e) {
      toast.error("Failed to load supplier orders");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadSuppliers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/suppliers`, { headers });
      setSupplierPortalList(r.data.suppliers || []);
    } catch (e) {
      // Non-fatal — fallback to distinct names from orders
      console.warn("Could not fetch supplier portal list", e);
    }
  }, [headers]);

  useEffect(() => {
    if (token) {
      load();
      loadSuppliers();
    }
  }, [token, load, loadSuppliers]);

  // merged supplier display-name list: portal (canonical) + any historical names on orders
  const supplierNames = useMemo(() => {
    const set = new Set();
    supplierPortalList.forEach((s) => s?.name && set.add(s.name));
    orders.forEach((o) => o?.supplier_name && set.add(o.supplier_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders, supplierPortalList]);

  // Unique hospital names for the Hospital filter dropdown
  const hospitalNames = useMemo(() => {
    const set = new Set();
    orders.forEach((o) => o?.hospital_name && set.add(o.hospital_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const filtered = useMemo(() => {
    let list = orders.filter((o) => {
      if (supplierFilter !== "all" && o.supplier_name !== supplierFilter) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (hospitalFilter !== "all" && o.hospital_name !== hospitalFilter) return false;
      if (search) {
        const hay = `${o.po_number || ""} ${o.description || ""} ${o.hospital_name || ""} ${o.hospital_po || ""} ${o.tracking_number || ""}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
    const cmp = (a, b) => {
      switch (sortBy) {
        case "hospital":
          return (a.hospital_name || "").localeCompare(b.hospital_name || "") || (a.po_number || "").localeCompare(b.po_number || "");
        case "supplier":
          return (a.supplier_name || "").localeCompare(b.supplier_name || "") || (a.po_number || "").localeCompare(b.po_number || "");
        case "po":
          return (a.po_number || "").localeCompare(b.po_number || "");
        case "aging":
          return (b.aging_days || 0) - (a.aging_days || 0);
        case "created":
        default:
          return (b.created_at || "").localeCompare(a.created_at || "");
      }
    };
    return list.slice().sort(cmp);
  }, [orders, supplierFilter, statusFilter, hospitalFilter, search, sortBy]);

  const filteredTotal = filtered.reduce((s, o) => s + (Number(o.ext_cost) || 0), 0);
  const filteredMargin = filtered.reduce((s, o) => s + (o.margin || 0), 0);
  const filteredOpen = filtered.filter((o) => !["delivered", "paid", "cancelled"].includes(o.status));
  const overdueCount = filtered.filter((o) => (o.aging_days || 0) > 14 && !["delivered", "paid", "cancelled"].includes(o.status)).length;

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/api/admin/supplier-orders/${orderId}`, { status: newStatus }, { headers });
      toast.success(`Order updated to ${STATUS_META[newStatus]?.label || newStatus}`);
      load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (order) => {
    if (!window.confirm(`Delete supplier order PO ${order.po_number || order.id}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/supplier-orders/${order.id}`, { headers });
      toast.success("Order deleted");
      load();
    } catch (e) {
      toast.error("Failed to delete order");
    }
  };

  const handleExport = async () => {
    const params = new URLSearchParams();
    if (supplierFilter !== "all") params.set("supplier_name", supplierFilter);
    const url = `${API}/api/admin/supplier-orders/export-excel?${params.toString()}`;
    try {
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error("Export failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = (r.headers.get("content-disposition") || "").match(/filename="?([^"]+)"?/)?.[1] || "supplier_orders.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel exported");
    } catch (e) {
      toast.error("Failed to export Excel");
    }
  };

  // === Bulk selection ===
  const toggleSelect = (orderId) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds((s) => {
      const allSelected = filtered.every((o) => s.has(o.id));
      if (allSelected) return new Set();
      return new Set(filtered.map((o) => o.id));
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    try {
      const res = await axios.post(
        `${API}/api/admin/supplier-orders/bulk-update-status`,
        { order_ids: Array.from(selectedIds), status: bulkStatus },
        { headers }
      );
      toast.success(`Updated ${res.data.updated} orders → ${STATUS_META[bulkStatus]?.label}`);
      setBulkStatus("");
      load();
    } catch (e) {
      toast.error("Bulk status update failed");
    }
  };

  const handleBulkSupplierEmail = async (statusToSet = null) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await axios.post(
        `${API}/api/admin/supplier-orders/bulk-email-supplier-draft`,
        { order_ids: Array.from(selectedIds), status: statusToSet },
        { headers }
      );
      const drafts = res.data.drafts || [];
      if (drafts.length === 0) {
        toast.error("No matching orders");
        return;
      }
      if (res.data.mixed_suppliers) {
        toast.info("Multiple suppliers selected — opening drafts for each");
      }
      setSupplierEmailDraft({ drafts, mixed: res.data.mixed_suppliers });
      if (statusToSet) load(); // refresh list since status changed
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to draft email");
    }
  };

  const openHospitalStatusEmail = async (order) => {
    try {
      const res = await axios.get(
        `${API}/api/admin/supplier-orders/${order.id}/email-draft`,
        { headers }
      );
      setStatusEmailOrder({ order, ...res.data });
    } catch (e) {
      toast.error("Failed to load contacts");
    }
  };

  return (
    <div className="space-y-5" data-testid="supplier-orders-tracker">
      {/* === Summary cards === */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Total Orders" value={filtered.length} sub={`${filteredOpen.length} open`} icon={ClipboardList} accent="blue" />
        <SummaryCard label="Open Cost" value={formatMoney(filteredOpen.reduce((s, o) => s + (Number(o.ext_cost) || 0), 0))} sub="Awaiting fulfillment" accent="amber" />
        <SummaryCard label="Total Cost" value={formatMoney(filteredTotal)} sub="All shown orders" accent="slate" />
        <SummaryCard
          label="Overdue >14d"
          value={overdueCount}
          sub={overdueCount > 0 ? "⚠ Action needed" : "All on track"}
          icon={AlertCircle}
          accent={overdueCount > 0 ? "red" : "emerald"}
          testid="overdue-count-card"
        />
      </div>

      {/* Margin row only if any line has it */}
      {filteredMargin !== 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-sm text-emerald-800 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          Live profit margin (hospital invoice − supplier cost − shipping):{" "}
          <span className="font-bold ml-1" data-testid="filtered-margin-total">{formatMoney(filteredMargin)}</span>
        </div>
      )}

      {/* === Bulk action toolbar (visible only when selections exist) === */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-600 text-white rounded-lg px-4 py-3 flex flex-wrap items-center gap-3 shadow-md" data-testid="bulk-action-toolbar">
          <span className="text-sm font-semibold">
            {selectedIds.size} selected
          </span>
          <button
            onClick={clearSelection}
            className="text-blue-100 hover:text-white text-xs underline"
            data-testid="clear-selection-btn"
          >
            Clear
          </button>
          <div className="h-5 w-px bg-blue-400/50" />
          <select
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.target.value)}
            className="bg-white text-slate-800 rounded px-2 py-1 text-sm"
            data-testid="bulk-status-select"
          >
            <option value="">Bulk status update...</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_META[s].label}</option>
            ))}
          </select>
          <Button
            onClick={handleBulkStatusUpdate}
            disabled={!bulkStatus}
            size="sm"
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="apply-bulk-status-btn"
          >
            Apply
          </Button>
          <div className="h-5 w-px bg-blue-400/50" />
          <Button
            onClick={() => handleBulkSupplierEmail(null)}
            size="sm"
            variant="outline"
            className="bg-white text-slate-800 hover:bg-slate-100 gap-1"
            data-testid="draft-supplier-email-btn"
          >
            <Send className="w-3 h-3" />
            Draft Supplier Email
          </Button>
          <Button
            onClick={() => handleBulkSupplierEmail("submitted_to_supplier")}
            size="sm"
            variant="outline"
            className="bg-white text-slate-800 hover:bg-slate-100 gap-1"
            data-testid="mark-submitted-and-email-btn"
          >
            <Send className="w-3 h-3" />
            Mark Submitted &amp; Email
          </Button>
        </div>
      )}

      {/* === Filter bar === */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search PO #, hospital, description, tracking..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="supplier-orders-search"
          />
        </div>

        <select
          value={supplierFilter}
          onChange={(e) => setSupplierFilter(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="supplier-filter"
        >
          <option value="all">All suppliers</option>
          {supplierNames.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        <select
          value={hospitalFilter}
          onChange={(e) => setHospitalFilter(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="hospital-filter"
        >
          <option value="all">All hospitals</option>
          {hospitalNames.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="status-filter"
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="sort-by"
          title="Sort orders"
        >
          <option value="created">Sort: Newest first</option>
          <option value="hospital">Sort: Hospital (A→Z)</option>
          <option value="supplier">Sort: Supplier (A→Z)</option>
          <option value="po">Sort: PO # (asc)</option>
          <option value="aging">Sort: Aging (oldest first)</option>
        </select>

        <Button
          onClick={handleSyncDashboards}
          disabled={syncing}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white gap-2 shadow-md"
          data-testid="sync-dashboards-btn"
          title="Push the latest supplier-order data to the hospital portal and admin hospital dashboards. Backfills hospital links and refreshes derived fields."
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync Dashboards"}
        </Button>

        <Button
          onClick={() => setShowPasteReplyModal(true)}
          variant="outline"
          className="gap-2"
          data-testid="paste-reply-btn"
          title="Paste a supplier reply email — system auto-detects PO numbers and marks them replied"
        >
          <MessageSquareReply className="w-4 h-4" />
          Paste Reply
        </Button>

        <Button
          onClick={() => setShowImportModal(true)}
          variant="outline"
          className="gap-2"
          data-testid="import-excel-btn"
        >
          <Upload className="w-4 h-4" />
          Import Excel
        </Button>

        <Button
          onClick={handleExport}
          variant="outline"
          className="gap-2"
          data-testid="export-supplier-orders-excel"
        >
          <Download className="w-4 h-4" />
          Export Excel
        </Button>

        <Button
          onClick={() => { setEditing(null); setShowAddModal(true); }}
          className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          data-testid="add-supplier-order-btn"
        >
          <Plus className="w-4 h-4" />
          New Order
        </Button>
      </div>

      {/* === Status pipeline strip === */}
      {summary && (
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {STATUS_OPTIONS.map((s) => {
            const cnt = summary.by_status?.[s]?.count || 0;
            const total = summary.by_status?.[s]?.total || 0;
            return (
              <button
                key={s}
                onClick={() => setStatusFilter(s === statusFilter ? "all" : s)}
                data-testid={`pipeline-pill-${s}`}
                className={`text-left px-3 py-2 rounded-md border text-xs transition-all ${STATUS_META[s].chip} ${
                  statusFilter === s ? "ring-2 ring-offset-1 ring-slate-700" : "opacity-90 hover:opacity-100"
                }`}
              >
                <div className="font-bold">{STATUS_META[s].label}</div>
                <div className="text-[10px] mt-0.5">{cnt} · {formatMoney(total)}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* === Orders table === */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="supplier-orders-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-600 text-xs uppercase tracking-wide">
                <th className="px-3 py-2 text-center w-8">
                  <button
                    onClick={selectAllVisible}
                    className="text-slate-400 hover:text-blue-600"
                    data-testid="select-all-checkbox"
                    title="Select all visible"
                  >
                    {filtered.length > 0 && filtered.every((o) => selectedIds.has(o.id))
                      ? <CheckSquare className="w-4 h-4 text-blue-600" />
                      : <Square className="w-4 h-4" />}
                  </button>
                </th>
                <th className="px-3 py-2 text-left">PO #</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">Supplier</th>
                <th className="px-3 py-2 text-left">Hospital</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2 text-right">Cost</th>
                <th className="px-3 py-2 text-right">Ship</th>
                <th className="px-3 py-2 text-right">Margin</th>
                <th className="px-3 py-2 text-right" title="Price billed to the hospital. This is the ONLY price the hospital sees in their portal.">Hospital Price</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-center">Aging</th>
                <th className="px-3 py-2 text-left">Tracking</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={14} className="text-center py-8 text-slate-400">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={14} className="text-center py-12 text-slate-400">
                  No orders match the current filters.
                </td></tr>
              )}
              {!loading && filtered.map((o) => (
                <tr key={o.id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedIds.has(o.id) ? "bg-blue-50/40" : ""}`} data-testid={`order-row-${o.po_number || o.id}`}>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => toggleSelect(o.id)}
                      className="text-slate-400 hover:text-blue-600"
                      data-testid={`select-order-${o.po_number}`}
                    >
                      {selectedIds.has(o.id)
                        ? <CheckSquare className="w-4 h-4 text-blue-600" />
                        : <Square className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-700">{o.po_number || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{o.order_date || "—"}</td>
                  <td className="px-3 py-2 text-slate-700 font-medium">{o.supplier_name}</td>
                  <td className="px-3 py-2">
                    {o.hospital_name ? (
                      <button
                        type="button"
                        onClick={() => openHospitalStatusEmail(o)}
                        className="text-blue-600 hover:underline inline-flex items-center gap-1 text-left"
                        data-testid={`hospital-link-${o.po_number}`}
                        title="Pick a contact and draft an email"
                      >
                        <Mail className="w-3 h-3" />
                        {o.hospital_name}
                      </button>
                    ) : "—"}
                    {o.hospital_po && <div className="text-xs text-slate-400">{o.hospital_po}</div>}
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    <div className="text-slate-700 truncate" title={o.description}>{o.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{o.qty || "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">{formatMoney(o.ext_cost)}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    {(o.shipping_cost_type === "free" || !o.shipping_cost_type) ? (
                      <span className="text-emerald-600">Free</span>
                    ) : o.shipping_cost_type === "third_party" ? (
                      <span className="text-blue-600" title="Third-party / hospital pays carrier">3rd-party</span>
                    ) : (
                      <span className="text-slate-700">{formatMoney(o.shipping_cost)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {o.margin === null || o.margin === undefined ? (
                      <span className="text-slate-300">—</span>
                    ) : (
                      <span className={o.margin >= 0 ? "text-emerald-600 font-semibold" : "text-red-600 font-semibold"}>
                        {formatMoney(o.margin)}
                      </span>
                    )}
                  </td>
                  <td
                    className="px-3 py-2 text-right"
                    title="Price billed to the hospital. Visible to hospital portal users once set."
                    data-testid={`hospital-price-${o.po_number}`}
                  >
                    {o.hospital_invoice_amount === null || o.hospital_invoice_amount === undefined || o.hospital_invoice_amount === "" ? (
                      <span className="text-amber-600 text-xs font-semibold" title="Hospital won't see this order until you set a price.">
                        Pricing pending
                      </span>
                    ) : (
                      <span className="font-bold text-blue-700">
                        {formatMoney(o.hospital_invoice_amount)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={o.status}
                      onChange={(e) => handleStatusChange(o.id, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 ${STATUS_META[o.status]?.chip || ""}`}
                      data-testid={`status-select-${o.po_number}`}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>{STATUS_META[s].label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <AgingBadge days={o.aging_days} status={o.status} />
                    {o.replied_at && (
                      <div
                        className="mt-1 text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-bold"
                        title={`Supplier replied ${new Date(o.replied_at).toLocaleString()}`}
                        data-testid={`replied-badge-${o.po_number}`}
                      >
                        <MessageSquareReply className="w-2.5 h-2.5" />
                        Replied
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {o.tracking_number || "—"}
                    {o.ship_method && <div className="text-slate-400">{o.ship_method}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => openHospitalStatusEmail(o)}
                        className="p-1 text-slate-500 hover:text-emerald-600"
                        data-testid={`email-status-${o.po_number}`}
                        title="Email status to hospital"
                      ><Send className="w-4 h-4" /></button>
                      <button
                        onClick={() => { setEditing(o); setShowAddModal(true); }}
                        className="p-1 text-slate-500 hover:text-blue-600"
                        data-testid={`edit-${o.po_number}`}
                        title="Edit"
                      ><Pencil className="w-4 h-4" /></button>
                      <button
                        onClick={() => handleDelete(o)}
                        className="p-1 text-slate-500 hover:text-red-600"
                        data-testid={`delete-${o.po_number}`}
                        title="Delete"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* === Add / Edit modal === */}
      {showAddModal && (
        <OrderModal
          headers={headers}
          editing={editing}
          existingSuppliers={supplierNames}
          supplierPortalList={supplierPortalList}
          onSuppliersChanged={loadSuppliers}
          onClose={() => { setShowAddModal(false); setEditing(null); }}
          onSaved={() => { setShowAddModal(false); setEditing(null); load(); }}
        />
      )}

      {/* === Excel import modal === */}
      {showImportModal && (
        <ExcelImportModal
          headers={headers}
          existingSuppliers={supplierNames}
          onClose={() => setShowImportModal(false)}
          onComplete={() => { setShowImportModal(false); load(); }}
        />
      )}

      {/* === Paste supplier reply modal === */}
      {showPasteReplyModal && (
        <PasteReplyModal
          headers={headers}
          existingSuppliers={supplierNames}
          onClose={() => setShowPasteReplyModal(false)}
          onApplied={() => { setShowPasteReplyModal(false); load(); }}
        />
      )}

      {/* === Hospital email-status modal === */}
      {statusEmailOrder && (
        <HospitalEmailStatusModal
          headers={headers}
          payload={statusEmailOrder}
          onClose={() => setStatusEmailOrder(null)}
        />
      )}

      {/* === Supplier bulk-email modal === */}
      {supplierEmailDraft && (
        <SupplierEmailDraftModal
          drafts={supplierEmailDraft.drafts}
          mixed={supplierEmailDraft.mixed}
          onClose={() => { setSupplierEmailDraft(null); clearSelection(); }}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, sub, icon: Icon, accent = "slate", testid }) => {
  const accentClasses = {
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
  }[accent];
  return (
    <div className={`border rounded-lg p-4 ${accentClasses}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide font-semibold opacity-80">{label}</span>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
};

const OrderModal = ({ headers, editing, existingSuppliers, supplierPortalList, onSuppliersChanged, onClose, onSaved }) => {
  const isEdit = !!editing;
  const [form, setForm] = useState(() => ({
    supplier_name: editing?.supplier_name || existingSuppliers[0] || "Lux Medical Group",
    po_number: editing?.po_number || "",
    order_date: editing?.order_date || new Date().toISOString().slice(0, 10),
    hospital_name: editing?.hospital_name || "",
    hospital_email: editing?.hospital_email || "",
    hospital_po: editing?.hospital_po || "",
    hospital_invoice: editing?.hospital_invoice || "",
    description: editing?.description || "",
    qty: editing?.qty || "",
    unit_cost: editing?.unit_cost ?? "",
    ext_cost: editing?.ext_cost ?? "",
    hospital_invoice_amount: editing?.hospital_invoice_amount ?? "",
    shipping_cost_type: editing?.shipping_cost_type || "free",
    shipping_cost: editing?.shipping_cost ?? 0,
    status: editing?.status || "open",
    expected_ship_date: editing?.expected_ship_date || "",
    delivered_date: editing?.delivered_date || "",
    tracking_number: editing?.tracking_number || "",
    ship_method: editing?.ship_method || "",
    notes: editing?.notes || "",
  }));
  const [autoCreateHs, setAutoCreateHs] = useState(false);
  const [hospitalCheck, setHospitalCheck] = useState(null); // {exists, match}
  const [saving, setSaving] = useState(false);
  const [showQuickAddSupplier, setShowQuickAddSupplier] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSupplierSelectChange = (val) => {
    if (val === "__add_new__") {
      setShowQuickAddSupplier(true);
      return;
    }
    setField("supplier_name", val);
  };

  const handleQuickAdded = (supplier) => {
    setShowQuickAddSupplier(false);
    if (supplier?.name) {
      setField("supplier_name", supplier.name);
    }
    if (typeof onSuppliersChanged === "function") onSuppliersChanged();
  };

  // Check hospital existence when name leaves focus
  const checkHospital = useCallback(async () => {
    if (!form.hospital_name || isEdit) return;
    try {
      const r = await axios.get(`${API}/api/admin/supplier-orders/check-hospital`, {
        headers,
        params: { name: form.hospital_name },
      });
      setHospitalCheck(r.data);
      if (!r.data.exists) setAutoCreateHs(true);
    } catch {
      setHospitalCheck(null);
    }
  }, [form.hospital_name, headers, isEdit]);

  const submit = async () => {
    if (!form.supplier_name || !form.description) {
      toast.error("Supplier name and description are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
        ext_cost: form.ext_cost === "" ? null : Number(form.ext_cost),
        hospital_invoice_amount: form.hospital_invoice_amount === "" ? null : Number(form.hospital_invoice_amount),
        shipping_cost: form.shipping_cost_type === "actual" ? (Number(form.shipping_cost) || 0) : 0,
        shipping_cost_type: form.shipping_cost_type || "free",
        auto_create_health_system: !isEdit && autoCreateHs && form.hospital_name && (!hospitalCheck || !hospitalCheck.exists),
      };
      if (isEdit) {
        await axios.patch(`${API}/api/admin/supplier-orders/${editing.id}`, payload, { headers });
        toast.success("Order updated");
      } else {
        await axios.post(`${API}/api/admin/supplier-orders`, payload, { headers });
        toast.success(payload.auto_create_health_system ? "Order created · hospital profile added to CRM" : "Order created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="supplier-order-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800">
            {isEdit ? `Edit Order ${editing.po_number || ""}` : "New Supplier Order"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-order-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Supplier *" testid="modal-supplier-name">
            <div className="flex gap-2">
              <select
                value={existingSuppliers.includes(form.supplier_name) ? form.supplier_name : (form.supplier_name ? "__custom__" : "")}
                onChange={(e) => onSupplierSelectChange(e.target.value)}
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                data-testid="modal-supplier-select"
              >
                <option value="" disabled>— pick a supplier —</option>
                {existingSuppliers.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
                {form.supplier_name && !existingSuppliers.includes(form.supplier_name) && (
                  <option value="__custom__">{form.supplier_name} (custom)</option>
                )}
                <option value="__add_new__">+ Add New Supplier…</option>
              </select>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowQuickAddSupplier(true)}
                className="gap-1 whitespace-nowrap"
                data-testid="modal-add-new-supplier-btn"
              >
                <UserPlus className="w-3 h-3" /> New
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Pulls from your <strong>Supplier Portal</strong> ({supplierPortalList?.length || 0} active). Adding new here saves to that list too.
            </p>
          </Field>
          <Field label="PO #" testid="modal-po-number">
            <input value={form.po_number} onChange={(e) => setField("po_number", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Order Date" testid="modal-order-date">
            <input type="date" value={form.order_date} onChange={(e) => setField("order_date", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Status" testid="modal-status">
            <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm">
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </Field>
          <Field label="Hospital / Customer" testid="modal-hospital">
            <input
              value={form.hospital_name}
              onChange={(e) => { setField("hospital_name", e.target.value); setHospitalCheck(null); }}
              onBlur={checkHospital}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
            {!isEdit && form.hospital_name && hospitalCheck && (
              hospitalCheck.exists ? (
                <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="hospital-found-hint">
                  <CheckSquare className="w-3 h-3" />
                  Linked to <strong>{hospitalCheck.match.name}</strong> · {hospitalCheck.match.contacts.length} contact(s)
                </p>
              ) : (
                <div className="mt-1 bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-800" data-testid="hospital-not-found-prompt">
                  <strong>Not in CRM yet.</strong> Build a profile so you can email contacts later?
                  <label className="flex items-center gap-1 mt-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoCreateHs}
                      onChange={(e) => setAutoCreateHs(e.target.checked)}
                      data-testid="auto-create-hs-checkbox"
                    />
                    <span>Auto-create Health System on save</span>
                  </label>
                </div>
              )
            )}
          </Field>
          <Field label="Hospital Email (for mailto)" testid="modal-hospital-email">
            <input type="email" value={form.hospital_email} onChange={(e) => setField("hospital_email", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            {hospitalCheck?.exists && Array.isArray(hospitalCheck.match?.contacts) && hospitalCheck.match.contacts.length > 0 && (
              <select
                onChange={(e) => {
                  const c = hospitalCheck.match.contacts.find((x) => x.id === e.target.value);
                  if (c?.email) setField("hospital_email", c.email);
                }}
                defaultValue=""
                className="mt-1 w-full border border-slate-300 rounded-md px-3 py-1.5 text-xs bg-blue-50"
                data-testid="modal-pick-contact"
              >
                <option value="" disabled>↳ Pick a saved contact ({hospitalCheck.match.contacts.length})…</option>
                {hospitalCheck.match.contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {(c.name || c.email)} {c.department ? `· ${c.department}` : ""} {c.is_primary ? "★" : ""}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Hospital PO #" testid="modal-hospital-po">
            <input value={form.hospital_po} onChange={(e) => setField("hospital_po", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Hospital Invoice #" testid="modal-hospital-invoice">
            <input value={form.hospital_invoice} onChange={(e) => setField("hospital_invoice", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description *" testid="modal-description">
              <input value={form.description} onChange={(e) => setField("description", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </Field>
          </div>
          <Field label="Qty (e.g. '5 Cases')" testid="modal-qty">
            <input value={form.qty} onChange={(e) => setField("qty", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Total Cost ($)" testid="modal-ext-cost">
            <input type="number" step="0.01" value={form.ext_cost} onChange={(e) => setField("ext_cost", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Hospital Invoice $ (for margin)" testid="modal-hospital-invoice-amount">
            <input type="number" step="0.01" value={form.hospital_invoice_amount} onChange={(e) => setField("hospital_invoice_amount", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <div className="sm:col-span-2 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="shipping-cost-section">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                Shipping Cost — never commissionable
              </label>
              <span className="text-[10px] text-amber-700">deducted from GP before commission</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { v: "free", l: "Free Shipping ($0)" },
                { v: "third_party", l: "Third-Party (hospital pays carrier)" },
                { v: "actual", l: "Actual Cost" },
              ].map((opt) => (
                <label key={opt.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="shipping_cost_type"
                    value={opt.v}
                    checked={form.shipping_cost_type === opt.v}
                    onChange={(e) => {
                      setField("shipping_cost_type", e.target.value);
                      if (e.target.value !== "actual") setField("shipping_cost", 0);
                    }}
                    data-testid={`shipping-type-${opt.v}`}
                  />
                  <span className="text-slate-700">{opt.l}</span>
                </label>
              ))}
            </div>
            {form.shipping_cost_type === "actual" && (
              <div className="mt-2 max-w-xs">
                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Shipping $ paid by SterileMate</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.shipping_cost}
                  onChange={(e) => setField("shipping_cost", e.target.value)}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  data-testid="shipping-cost-input"
                />
              </div>
            )}
          </div>
          <Field label="Expected Ship Date" testid="modal-expected-ship">
            <input type="date" value={form.expected_ship_date} onChange={(e) => setField("expected_ship_date", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Tracking #" testid="modal-tracking">
            <input value={form.tracking_number} onChange={(e) => setField("tracking_number", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Ship Method" testid="modal-ship-method">
            <input value={form.ship_method} onChange={(e) => setField("ship_method", e.target.value)} placeholder="UPS, FedEx 4/21..." className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Notes" testid="modal-notes">
              <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </Field>
          </div>
        </div>
        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="cancel-order-modal">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-order-btn">
            {saving ? "Saving..." : (isEdit ? "Save Changes" : "Create Order")}
          </Button>
        </div>
      </div>
      {showQuickAddSupplier && (
        <QuickAddSupplierModal
          headers={headers}
          onClose={() => setShowQuickAddSupplier(false)}
          onAdded={handleQuickAdded}
        />
      )}
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
    {children}
  </div>
);

// =============================================================================
// Quick-Add Supplier modal — invoked from the Order modal.
// Saves a stub into db.suppliers (no setup-email/invite flow) so the new name
// appears in BOTH the Supplier Portal admin list AND this dropdown right away.
// =============================================================================
const QuickAddSupplierModal = ({ headers, onClose, onAdded }) => {
  const [form, setForm] = useState({ name: "", contact_name: "", email: "", phone: "", website: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Supplier name is required");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/admin/suppliers/quick`, form, { headers });
      if (r.data.created) {
        toast.success(`${r.data.supplier.name} added to Supplier Portal`);
      } else {
        toast.info(`${r.data.supplier.name} already exists — selected`);
      }
      onAdded(r.data.supplier);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add supplier");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="quick-add-supplier-modal">
      <div className="bg-white rounded-lg w-full max-w-md my-16 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-emerald-600" />
            Add New Supplier
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-slate-500">
            Saves to <strong>Supplier Portal</strong> instantly. No invite email is sent —
            you can promote them to a full portal account later from <em>Admin → Suppliers</em>.
          </p>
          <Field label="Supplier Name *">
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="e.g. Eviva Imaging Group" data-testid="quick-supplier-name" />
          </Field>
          <Field label="Primary Contact">
            <input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="e.g. Joey Calhoun" data-testid="quick-supplier-contact" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Email">
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="orders@eviva.com" data-testid="quick-supplier-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="(281) 555-0100" data-testid="quick-supplier-phone" />
            </Field>
          </div>
          <Field label="Website">
            <input value={form.website} onChange={(e) => set("website", e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder="https://..." data-testid="quick-supplier-website" />
          </Field>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="quick-supplier-save-btn">
            {saving ? "Saving..." : "Add Supplier"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Hospital Email Status modal — pick contacts → opens Outlook with mailto:
// =============================================================================
const HospitalEmailStatusModal = ({ headers, payload, onClose }) => {
  const { order, subject: initialSubject, body: initialBody, hospital_match, contacts } = payload;
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState(initialBody);
  const [selectedContactIds, setSelectedContactIds] = useState(() => {
    // pre-select primary contact, or all if none flagged primary
    const primary = (contacts || []).filter((c) => c.is_primary).map((c) => c.id);
    return new Set(primary.length > 0 ? primary : (contacts || []).slice(0, 1).map((c) => c.id));
  });
  const [adhocEmail, setAdhocEmail] = useState("");
  const [adhocList, setAdhocList] = useState([]);
  // Inline new-contact state
  const [showNewContact, setShowNewContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", department: "Supply Chain" });
  const [saving, setSaving] = useState(false);

  const allContacts = contacts || [];
  const groupedByDept = useMemo(() => {
    const out = {};
    for (const c of allContacts) {
      const d = c.department || "General";
      out[d] = out[d] || [];
      out[d].push(c);
    }
    return out;
  }, [allContacts]);

  const toggleContact = (id) => {
    setSelectedContactIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addAdhoc = () => {
    const v = adhocEmail.trim();
    if (!v || !v.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (adhocList.includes(v)) {
      toast.info("Already added");
      return;
    }
    setAdhocList((a) => [...a, v]);
    setAdhocEmail("");
  };

  const saveNewContact = async () => {
    if (!hospital_match?.id) {
      toast.error("Cannot save contact — hospital profile missing. Save as ad-hoc email instead.");
      return;
    }
    if (!newContact.email || !newContact.email.includes("@")) {
      toast.error("Email is required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/api/health-systems/${hospital_match.id}/contacts`,
        newContact,
        { headers }
      );
      const created = res.data.contact;
      toast.success(`${created.name || created.email} added to ${hospital_match.name}`);
      // Add to live selection
      allContacts.push(created);
      setSelectedContactIds((s) => new Set([...s, created.id]));
      setNewContact({ name: "", email: "", phone: "", department: "Supply Chain" });
      setShowNewContact(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save contact");
    } finally {
      setSaving(false);
    }
  };

  const recipients = useMemo(() => {
    const fromContacts = allContacts.filter((c) => selectedContactIds.has(c.id)).map((c) => c.email);
    return [...new Set([...fromContacts, ...adhocList])].filter(Boolean);
  }, [allContacts, selectedContactIds, adhocList]);

  const sendViaOutlook = () => {
    if (recipients.length === 0) {
      toast.error("Select at least one recipient");
      return;
    }
    const to = encodeURIComponent(recipients.join(","));
    const sub = encodeURIComponent(subject);
    const bod = encodeURIComponent(body);
    const url = `mailto:${to}?subject=${sub}&body=${bod}`;
    if (url.length > 1900) {
      toast.warning("Body is long — Outlook may truncate. Consider shortening.");
    }
    window.location.href = url;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="hospital-email-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800">
              Email Status to {hospital_match?.name || order.hospital_name || "Hospital"}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">PO {order.po_number} · {order.description?.slice(0, 60)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-hospital-email-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Hospital profile state */}
          {!hospital_match ? (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="no-hospital-profile-warning">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <strong>{order.hospital_name}</strong> isn't in your Health Systems CRM yet.
                You can still email ad-hoc addresses below, or click <em>Edit Order → Save</em>{" "}
                with "Auto-create profile" enabled to add them to the CRM.
              </div>
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-center gap-2" data-testid="hospital-profile-found">
              <CheckSquare className="w-4 h-4" />
              Linked to <strong>{hospital_match.name}</strong> · {(hospital_match.domains || []).join(", ") || "no domains set"} · {allContacts.length} contact(s)
            </div>
          )}

          {/* Contact picker grouped by department */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-bold text-slate-700">Recipients</label>
              {hospital_match?.id && (
                <button
                  onClick={() => setShowNewContact((v) => !v)}
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  data-testid="add-contact-toggle"
                >
                  <UserPlus className="w-3 h-3" />
                  {showNewContact ? "Cancel" : "Add new contact"}
                </button>
              )}
            </div>

            {allContacts.length === 0 ? (
              <p className="text-xs text-slate-400 italic mb-2">No saved contacts. Add an ad-hoc email below or save a new contact.</p>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto p-2 space-y-2" data-testid="contact-picker">
                {Object.entries(groupedByDept).map(([dept, list]) => (
                  <div key={dept}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">{dept}</p>
                    {list.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedContactIds.has(c.id)}
                          onChange={() => toggleContact(c.id)}
                          data-testid={`contact-checkbox-${c.id}`}
                        />
                        <span className="text-sm text-slate-700 flex-1">
                          <strong>{c.name || c.email}</strong>
                          <span className="text-slate-400"> · {c.email}</span>
                          {c.is_primary && <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded uppercase font-bold">Primary</span>}
                        </span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {showNewContact && hospital_match?.id && (
              <div className="mt-3 border border-blue-200 bg-blue-50/50 rounded-lg p-3 grid grid-cols-2 gap-2" data-testid="new-contact-form">
                <input placeholder="Name" className="border border-slate-300 rounded px-2 py-1 text-sm col-span-2" value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} />
                <input placeholder="Email *" className="border border-slate-300 rounded px-2 py-1 text-sm" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
                <input placeholder="Phone" className="border border-slate-300 rounded px-2 py-1 text-sm" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} />
                <select value={newContact.department} onChange={(e) => setNewContact({ ...newContact, department: e.target.value })} className="border border-slate-300 rounded px-2 py-1 text-sm col-span-2">
                  {["Supply Chain", "Procurement", "EVS", "Operating Room", "Sterile Processing", "Materials Management", "Nursing", "Finance", "General"].map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <div className="col-span-2 flex justify-end">
                  <Button size="sm" onClick={saveNewContact} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-new-contact-btn">
                    {saving ? "Saving..." : "Save Contact"}
                  </Button>
                </div>
              </div>
            )}

            {/* Ad-hoc email entry */}
            <div className="mt-3">
              <p className="text-xs text-slate-500 mb-1">Or add ad-hoc emails:</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="someone@hospital.org"
                  value={adhocEmail}
                  onChange={(e) => setAdhocEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addAdhoc()}
                  className="flex-1 border border-slate-300 rounded-md px-3 py-1.5 text-sm"
                  data-testid="adhoc-email-input"
                />
                <Button size="sm" variant="outline" onClick={addAdhoc} data-testid="add-adhoc-btn">Add</Button>
              </div>
              {adhocList.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid="adhoc-list">
                  {adhocList.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded-full">
                      {e}
                      <button onClick={() => setAdhocList((a) => a.filter((x) => x !== e))} className="hover:text-red-600">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Editable subject + body */}
          <Field label="Subject" testid="modal-email-subject">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Body" testid="modal-email-body">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
          </Field>
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-between items-center bg-slate-50">
          <span className="text-xs text-slate-500">
            {recipients.length} recipient{recipients.length !== 1 ? "s" : ""}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cancel-hospital-email">Cancel</Button>
            <Button
              onClick={sendViaOutlook}
              disabled={recipients.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="open-outlook-btn"
            >
              <Mail className="w-4 h-4" />
              Open in Outlook
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Supplier Email Draft modal — bulk follow-up email per supplier
// =============================================================================
const SupplierEmailDraftModal = ({ drafts, mixed, onClose }) => {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = drafts[activeIdx];
  const [subject, setSubject] = useState(active?.subject || "");
  const [body, setBody] = useState(active?.body || "");
  const [supplierTo, setSupplierTo] = useState("");

  // sync when switching supplier tab
  useEffect(() => {
    setSubject(drafts[activeIdx]?.subject || "");
    setBody(drafts[activeIdx]?.body || "");
    setSupplierTo("");
  }, [activeIdx, drafts]);

  if (!active) return null;

  const open = () => {
    if (!supplierTo.includes("@")) {
      toast.error("Enter the supplier's email");
      return;
    }
    const url = `mailto:${encodeURIComponent(supplierTo)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="supplier-email-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Supplier Follow-up Email
            </h3>
            {mixed && (
              <p className="text-xs text-amber-700 mt-1">
                ⚠ Mixed suppliers selected — separate drafts per supplier shown below
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-supplier-email-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs if multiple suppliers */}
        {drafts.length > 1 && (
          <div className="border-b border-slate-200 px-6 flex gap-1 overflow-x-auto" data-testid="supplier-tabs">
            {drafts.map((d, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 ${
                  i === activeIdx ? "border-blue-600 text-blue-600 font-bold" : "border-transparent text-slate-500"
                }`}
                data-testid={`supplier-tab-${i}`}
              >
                {d.supplier_name} ({d.po_count})
              </button>
            ))}
          </div>
        )}

        <div className="px-6 py-5 space-y-4">
          <Field label={`Send to ${active.supplier_name} *`} testid="modal-supplier-to">
            <input
              type="email"
              placeholder={`orders@${active.supplier_name.toLowerCase().replace(/\s+/g, "")}.com`}
              value={supplierTo}
              onChange={(e) => setSupplierTo(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Subject" testid="modal-supplier-subject">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Body" testid="modal-supplier-body">
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono" />
          </Field>
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} data-testid="cancel-supplier-email">Cancel</Button>
          <Button onClick={open} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="open-supplier-outlook-btn">
            <Mail className="w-4 h-4" />
            Open in Outlook
          </Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Excel Import modal — preview then commit
// =============================================================================
const ExcelImportModal = ({ headers, existingSuppliers, onClose, onComplete }) => {
  const [supplier, setSupplier] = useState(existingSuppliers[0] || "Lux Medical Group");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildFormData = () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("supplier_name", supplier);
    return fd;
  };

  const doPreview = async () => {
    if (!file || !supplier) {
      toast.error("Pick a supplier and a file first");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/supplier-orders/import-excel/preview`, {
        method: "POST",
        headers,
        body: buildFormData(),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Preview failed");
      }
      const data = await r.json();
      setPreview(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/supplier-orders/import-excel/commit`, {
        method: "POST",
        headers,
        body: buildFormData(),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Import failed");
      }
      const data = await r.json();
      toast.success(`Imported ${data.created} new + ${data.updated} updated rows`);
      onComplete();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="excel-import-modal">
      <div className="bg-white rounded-lg w-full max-w-3xl my-10 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import Live Spreadsheet
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-import-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
            Match key: <strong>PO # + Supplier name</strong>. Existing rows update, new ones are inserted.
            Expected columns: PO #, Date, Hospital, Hospital PO#, Hospital Invoice, Description, Qty, Total Cost, Status, Tracking #, Ship Method.
          </div>

          <Field label="Supplier *" testid="import-supplier">
            <input
              list="import-supplier-list"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
            <datalist id="import-supplier-list">
              {existingSuppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
          </Field>

          <Field label="Excel file *" testid="import-file">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
            {file && <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </Field>

          {!preview && (
            <Button onClick={doPreview} disabled={!file || loading} className="bg-blue-600 hover:bg-blue-700" data-testid="preview-import-btn">
              {loading ? "Reading..." : "Preview Changes"}
            </Button>
          )}

          {preview && (
            <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid="import-preview">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-emerald-50 rounded p-2">
                  <div className="text-2xl font-bold text-emerald-700">{preview.to_create.length}</div>
                  <div className="text-xs text-emerald-700">Will create</div>
                </div>
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-2xl font-bold text-blue-700">{preview.to_update.length}</div>
                  <div className="text-xs text-blue-700">Will update</div>
                </div>
                <div className="bg-slate-50 rounded p-2">
                  <div className="text-2xl font-bold text-slate-600">{preview.unchanged.length}</div>
                  <div className="text-xs text-slate-600">Unchanged</div>
                </div>
              </div>
              {preview.to_update.length > 0 && (
                <div className="text-xs">
                  <p className="font-bold text-slate-700 mb-1">Updates ({preview.to_update.length}):</p>
                  <ul className="max-h-32 overflow-y-auto space-y-1">
                    {preview.to_update.slice(0, 8).map((u) => (
                      <li key={u.id} className="text-slate-600">
                        PO {u.po_number}: {Object.keys(u.changes).join(", ")}
                      </li>
                    ))}
                    {preview.to_update.length > 8 && <li className="text-slate-400 italic">+ {preview.to_update.length - 8} more...</li>}
                  </ul>
                </div>
              )}
              {preview.to_create.length > 0 && (
                <div className="text-xs">
                  <p className="font-bold text-emerald-700 mb-1">New ({preview.to_create.length}):</p>
                  <ul className="max-h-32 overflow-y-auto space-y-1">
                    {preview.to_create.slice(0, 6).map((c, i) => (
                      <li key={i} className="text-slate-600">
                        PO {c.po_number || "(no PO)"} — {(c.description || "").slice(0, 60)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="cancel-import">Cancel</Button>
          {preview && (
            <Button onClick={doCommit} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="commit-import-btn">
              {loading ? "Importing..." : `Import ${preview.to_update.length + preview.to_create.length} rows`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Paste Supplier Reply modal — auto-detects PO numbers + marks them replied
// =============================================================================
const PasteReplyModal = ({ headers, existingSuppliers, onClose, onApplied }) => {
  const [supplier, setSupplier] = useState("");
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    if (!text.trim()) {
      toast.error("Paste the supplier reply first");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/supplier-orders/paste-reply`,
        { reply_text: text, supplier_name: supplier || null },
        { headers }
      );
      setResult(r.data);
      if (r.data.modified_count > 0) {
        toast.success(`Marked ${r.data.modified_count} order(s) as replied · matched POs ${r.data.matched_pos.join(", ")}`);
      } else if (r.data.matched_pos.length === 0) {
        toast.warning("No PO numbers detected in pasted text");
      } else {
        toast.info(`POs detected (${r.data.matched_pos.join(", ")}) but none matched any existing orders`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply reply");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="paste-reply-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <MessageSquareReply className="w-5 h-5 text-emerald-600" />
            Paste Supplier Reply
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-paste-reply">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900">
            Paste the supplier's reply email (any format). The system auto-detects <code className="bg-white px-1 rounded">[SM-PO 1520,1521]</code> tags
            or "PO #1520" patterns and marks each order as replied.
          </div>

          <Field label="Filter by supplier (optional, prevents cross-supplier mismatch)" testid="paste-reply-supplier">
            <input
              list="paste-reply-suppliers"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              placeholder="e.g. Lux Medical Group (leave blank to match across all)"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            />
            <datalist id="paste-reply-suppliers">
              {existingSuppliers.map((s) => <option key={s} value={s} />)}
            </datalist>
          </Field>

          <Field label="Reply text *" testid="paste-reply-text">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={10}
              placeholder="Paste the supplier's reply email here..."
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>

          {result && (
            <div className="border border-slate-200 rounded p-3 text-xs space-y-1" data-testid="paste-reply-result">
              <p>POs detected: <strong>{result.matched_pos.join(", ") || "none"}</strong> {result.used_tag && <span className="text-emerald-600">(via [SM-PO] tag)</span>}</p>
              <p>Orders updated: <strong>{result.modified_count}</strong></p>
              {result.updated_orders?.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {result.updated_orders.map((o) => (
                    <li key={o.id} className="text-slate-700">✓ PO {o.po_number} ({o.supplier_name})</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={loading} data-testid="cancel-paste-reply">Cancel</Button>
          {result && result.modified_count > 0 ? (
            <Button onClick={onApplied} className="bg-emerald-600 hover:bg-emerald-700" data-testid="paste-reply-done">Done</Button>
          ) : (
            <Button onClick={apply} disabled={!text.trim() || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-paste-reply">
              {loading ? "Detecting..." : "Detect & Mark Replied"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierOrdersManager;
